import {injectable} from "inversify";
import {ShopService, TabName} from "@/services/shop-service";
import {TabbedPanel} from "@/scenes.ui/tabbed-panel";
import {Tr} from "@/i18n/i18n";
import PlayerStore from "@/store/player";
import {HeroShopService} from "@/services/hero-shop-service";
import {ShopCreaturePanel} from "@/scenes.ui/shop-creature-panel";
import ChainStore from "@/store/chain";
import {PaymentNotificationService} from "@/services/payment-notification-service";
import {ListService} from "@/services/list-service";
import {PaymentChangeService} from "@/services/payment-change-service";
import {IapService} from "@/services/iap-service";
import {Scene} from "phaser";
import {Chain, ChainDto, TaskOption} from "@/store/chain/types";
import Container = Phaser.GameObjects.Container;
import {BankOption} from "@/store/shop/types";
import {ChainTaskPanel} from "@/scenes.ui/chain-task-panel";
import ShopUtils from "@/utils/shop-utils";
import {IntegrationUtils} from "@/utils/integration-utils";
import {ChainPackPanel} from "@/scenes.ui/chain-pack-panel";
import PackUtils from "@/utils/pack-utils";
import {ChainUtils} from "@/utils/chain-utils";
import {ChestDescPanel} from "@/scenes.ui/chest-desc-panel";
import {Player} from "@/store/player/types";
import {HintDto} from "@/store/task/types";
import {TizerPanel} from "@/scenes.ui/tizer-panel";
import ListenerManager from "@/managers/listener-manager";
import CU from "@/utils/c-u";

@injectable()
export class ChainService extends ShopService {

    private chainId: number | undefined = undefined;
    private chainCont: Container | undefined = undefined;
    private static AUTOSCROLL_SPEED: number = -3.1;

    private openBankCallback!: () => unknown;

    public openChain(scene: Scene, canvas: HTMLCanvasElement, noValuteFeedback: boolean, closeCallback: () => void,
                    paymentNotificationService: PaymentNotificationService, statClickCallback: (creatureId: string) => unknown,
                    tabName: TabName, listService: ListService, paymentChangeService: PaymentChangeService,
                    iapService: IapService, chainId: number, chainCont: Container, openBankCallback: () => unknown) {
        this.chainId = chainId;
        this.chainCont = chainCont;
        this.openBankCallback = openBankCallback;
        this.openShop(scene, canvas, noValuteFeedback, closeCallback,
            paymentNotificationService, statClickCallback, tabName, listService, paymentChangeService, iapService);
    }

    protected createTabbedPanel(): TabbedPanel {
        let self = this;
        return new TabbedPanel(this.scene!, this.canvas!, '', ['bank', 'chain'],
            [Tr('bank'), ''], [true, true], this.selectSound, (tabId: string) => {
                if (tabId === 'bank') {
                    this.populateBankTab();
                } else if (tabId === 'chain') {
                    this.populateChainTab();
                }
            }, () => {}, () => self.closeCallback());
    }

    protected populateChainTab(): void {
        let self = this;

        let player = PlayerStore.getPlayer()!;
        this.setupHeroAgitations(player);

        ChainStore.fetchChainInfo(this.chainId!).then((dto: ChainDto) => {

            self.tabbedPanel.setTabText(1, dto.title);
            self.tabbedPanel.setAgitationText(dto.agitation);
            self.tabbedPanel.setTitle(dto.title);

            const options: BankOption[] = dto.elements;
            let panels: Container[] = [];
            const userSource = player.userSource;

            let panelY = 0;

            panels.push(...HeroShopService.getTopTextsByTabId(this.scene!, this.canvas!));

            let scp: Container | undefined = undefined;

            options.forEach(bo => {

                let chainItemType = ChainUtils.getChainItemType(panels.length, dto.currentIndex);
                let panel!: ChainTaskPanel | ChainPackPanel;
                let buyCallback = () => {
                    if (bo.price > 0) {
                        ShopUtils.buy(undefined, self.awardSound, player.userSource,
                            self.paymentNotificationService, self.iapService, self.paymentChangeService, bo, IntegrationUtils.isExternalUser());
                    } else {
                        PackUtils.visualizeTake(this.scene!, panel.getItems(), self.clickSound!);
                        panel.setRecieved(true);
                        panel.darken();
                        let showCongrats = false;
                        if (panels.indexOf(panel) + 1 < panels.length - 1) {
                            (panels[panels.indexOf(panel) + 1] as any).lighten(() => this.tabbedPanel.setScrollSpeed(ChainService.AUTOSCROLL_SPEED * CU.S));
                        } else {
                            showCongrats = true;
                        }
                        ChainStore.takeFreeChainOption(self.chainId!, bo.id).then((player: Player) => {
                            if (showCongrats) {
                                let self = this;
                                this.scene!.time.delayedCall(1000, () =>{
                                    if (self.scene!.scene.isActive()) {
                                        ChainStore.getCongratulation(self.chainId!).then((hint: HintDto) => {
                                            ListenerManager.callPurchaseRecievedCallbacl();
                                            let ip: TizerPanel = new TizerPanel(this.scene!, this.canvas!, hint, (show: boolean) => {
                                                ip.close();
                                                if (show) {
                                                    self.openBankCallback();
                                                }
                                                ChainStore.setSeen(self.chainId!);
                                                if (!!self.chainCont) {
                                                    self.chainCont.destroy();
                                                }
                                            });
                                            ip.openAnimated();
                                        });
                                    }
                                });
                            }
                        });
                    }
                };

                if (bo instanceof TaskOption) {
                    let to = bo as TaskOption;

                    panel = new ChainTaskPanel(this.scene!, 0, panelY, to, player, userSource, buyCallback, (creatureId: string) => {
                        self.listService.showManual(this.scene!, this.canvas!, creatureId, () => {});
                    }, chainItemType, panels.length);
                    scp = panel;

                } else {

                    panel = new ChainPackPanel(this.scene!, 0, panelY, bo, player, userSource, buyCallback, (creatureId: string) => {
                        self.listService.showManual(this.scene!, this.canvas!, creatureId, () => {});
                    }, chainItemType, panels.length);
                    scp = panel;
                }

                panels.push(scp!);
                panelY += ShopCreaturePanel.HEIGHT;
            });

            panels.push(new ChestDescPanel(this.scene!, this.canvas!));

            this.tabbedPanel.supplyChildren('chain', panels);

            this.tabbedPanel.setScrollSpeed(ChainService.AUTOSCROLL_SPEED * CU.S + (ChainService.AUTOSCROLL_SPEED + 1) * (dto.currentIndex - 1) * CU.S);
        });
    }

    private static findChain(chainId: number): Chain | undefined {
        for (let chain of PlayerStore.getPlayer().chains) {
            if (chain.id === chainId) {
                return chain;
            }
        }
        return undefined;
    }
}